import React, { useState, useEffect, useContext } from "react";
import styled from "styled-components";
import { Question, MultipleQuestionOptions, NumberQuestionOptions, MultipleTableQuestionOptions, QuestionType } from "consumerhouse-shared";
import AuthContext from "../AuthContext";
import { Condition } from "consumerhouse-shared";
import {
    faArrowLeft,
    faArrowRight,
    faHeading,
    faKeyboard,
    faList,
    faMapMarker,
    faQuestion,
    faSortNumericUpAlt,
    faTable,
    IconDefinition,
} from "@fortawesome/free-solid-svg-icons";
import {
    LocationAnswerValue,
    MultipleAnswerValue,
    MultipleTableAnswerValue,
    NumberAnswerValue,
    OpenAnswerValue,
    validateAnswer,
} from "consumerhouse-shared";
import { SatisfiesBadge } from "./Badge";
import { useLanguage } from "../useLanguage";
import { TitleQuestionDefinition } from "./questions/TitleQuestion";
import { OpenQuestionDefinition } from "./questions/OpenQuestion";
import { NumberQuestionDefinition } from "./questions/NumberQuestion";
import { MultipleTableQuestionDefinition } from "./questions/MultipleTableQuestion";
import { MultipleQuestionDefinition } from "./questions/MultipleQuestion";
import { LocationQuestionDefinition } from "./questions/LocationQuestion";
import { ImageQuestionDefinition } from "./questions/ImageQuestion";
import Button from "./Button";
import { ButtonContainer } from "./ButtonContainer";
import { EnsureInputDefinition } from "./questions/EnsureInputQuestion";
import sanitizeHtml from "sanitize-html";

export interface QuestionDefinition<Props = any> {
    icon: IconDefinition;
    defaultOptions: Props;
    questionComponent: React.FunctionComponent<QuestionProps<Props, any>>;
    optionsEditComponent?: React.FunctionComponent<OptionsEditProps<Props>>;
    conditionEditComponent?: React.FunctionComponent<ConditionEditProps<any, Props>>;
}

export const QUESTION_DEFINITIONS = {
    number: NumberQuestionDefinition,
    title: TitleQuestionDefinition,
    open: OpenQuestionDefinition,
    multiple: MultipleQuestionDefinition,
    multipletable: MultipleTableQuestionDefinition,
    location: LocationQuestionDefinition,
    image: ImageQuestionDefinition,
    ensure: EnsureInputDefinition,
};

type QuestionCardProps = {
    question: Question;
    initialValue?: object;
    readOnly?: boolean;
    accessToken?: string;
    calculateSatisfies?: boolean;
    onChange?: (value: any) => void;
};

const Container = styled.div`
    padding: 1.5em;
    margin: 2em 0;
    background: #0001;
    flex-direction: column;
    border-radius: 1em;
`;

const ConditionsContainer = styled.div`
    display: inline-block;
    border-radius: 1em;
    background: #0001;
    padding: 0.8em;
    font-size: 0.9em;
    font-weight: bold;
`;

export const QuestionNote = styled.p`
    display: inline-block;
    margin: 0.35em 0.5em 0.35em 0;
    color: #777;
    /* font-weight: bold; */
    font-size: 0.85em;
`;

export const QuestionContainer = styled.div`
    margin: 0.5em 0;
`;

export function QuestionAnswer(props: QuestionCardProps) {
    const { client } = useContext(AuthContext);
    const [value, setValue] = useState<object | undefined>(
        props.initialValue && validateAnswer(props.question.type, props.initialValue) ? props.initialValue : undefined
    );
    const [results, setResults] = useState<[boolean, string][]>([]);
    const language = useLanguage();

    async function submitAnswer() {
        await client.fetch(
            "/questionnaire/answer",
            {
                questionId: props.question.id!,
                value: value!,
            },
            "POST",
            props.accessToken
        );
    }

    useEffect(() => {
        if (value) {
            if (value !== props.initialValue && !props.readOnly) {
                props.onChange?.(value);
                submitAnswer();
            }

            if (props.calculateSatisfies) {
                if (!props.question.conditions) {
                    console.warn("[question-answer] (warning) showSatisfies: enabled, but conditions not available");
                } else {
                    setResults(props.question.conditions.map((e) => Condition.check(e, props.question.type, value)));
                }
            }
        }
    }, [value, props.initialValue, props.question, props.readOnly, props.calculateSatisfies]);

    const questionType = QUESTION_DEFINITIONS[props.question.type];
    if (!questionType) {
        console.warn(`invalid question type ${props.question.type}`);
        return null;
    }

    return (
        <Container>
            <h3>
                {results.length > 0 && <SatisfiesBadge satisfies={results?.every((e) => e[0])} />}
                <span
                    dangerouslySetInnerHTML={{
                        __html: sanitizeHtml(props.question.question, {
                            allowedClasses: {
                                "*": ["underline", "bold", "italic", "mark-yellow", "mark-red", "mark-blue", "mark-green"],
                            },
                            allowedAttributes: {
                                "*": ["style", "class"],
                            },
                            allowedStyles: {
                                "*": {
                                    color: [/^[#a-z0-9_ -]*$/i],
                                    "background-color": [/^[#a-z0-9_ -]*$/i],
                                    "font-weight": [/^[a-z0-9_. -]*$/i],
                                    "font-style": [/^[a-z0-9_. -]*$/i],
                                    "text-decoration": [/^[a-z0-9_. -]*$/i],
                                },
                            },
                        }),
                    }}></span>
            </h3>
            {!props.question.optional || ["title", "image"].includes(props.question.type) || (
                <QuestionNote>{language("question-optional")}</QuestionNote>
            )}
            <questionType.questionComponent value={value} onChange={setValue} question={props.question as Question<any>} readOnly={props.readOnly} />
            {results.length > 0 && (
                <ConditionsContainer>
                    <h3>Condities</h3>
                    {results.map((e, i) => (
                        <p key={i}>
                            <SatisfiesBadge satisfies={e[0]} /> {e[1]}
                        </p>
                    ))}
                </ConditionsContainer>
            )}
        </Container>
    );
}

export type OptionsEditProps<TOptions> = {
    options: TOptions | null;
    allQuestions: Question[];
    onChange: (options: TOptions) => void;
};

export type ConditionEditProps<TValue, TQuestion = any> = {
    question: Question<TQuestion>;
    condition: Condition<TValue | undefined>;
    onChange: (value: TValue) => void;
};

export type QuestionProps<TQuestion = any, TAnswer = any> = {
    readOnly?: boolean;
    question: Question<TQuestion>;
    value?: TAnswer;
    onChange: (value: TAnswer) => void;
};

function isJumpableQuestion(question: Question) {
    if (
        question.type === "multiple" &&
        (question.optionsJson as MultipleQuestionOptions).options.some((e) => typeof e !== "string" && e.jumpToParameter !== undefined)
    )
        return true;

    if (question.type === "ensure") return true;

    return false;
}

export function QuestionnaireFillIn(props: { questions: Question[]; accessToken?: string; onDone?: (stopped: boolean) => void; loading?: boolean }) {
    const [questions, setQuestions] = useState(props.questions);
    const [minIndex, setMinIndex] = useState(0);
    const [maxIndex, setMaxIndex] = useState(0);
    const language = useLanguage();

    function setPreviousQuestions() {
        let newMaxIndex = minIndex;
        let newMinIndex = 0;

        for (let i = newMaxIndex - 2; i >= 0; i--) {
            let question = questions[i];
            if (isJumpableQuestion(question)) {
                newMinIndex = i + 1;
                break;
            }
        }

        setMinIndex(newMinIndex);
        setMaxIndex(newMaxIndex);
    }

    function setNextQuestions() {
        if (maxIndex === questions.length) {
            props.onDone?.(false);
            return;
        }

        let newMinIndex = maxIndex;
        if (maxIndex >= 1) {
            let questionAnswer = questions[maxIndex - 1];

            if (questionAnswer.type === "multiple") {
                let answer = questionAnswer.answers?.[0]?.valueJson as MultipleAnswerValue;
                let options = (questionAnswer.optionsJson as MultipleQuestionOptions).options;

                for (let i = 0; i < options.length; i++) {
                    let option = options[i];

                    if (typeof option === "string" || option.jumpToParameter === undefined) continue;

                    if (answer.selected.includes(option.option)) {
                        // This is a selected option, jump

                        if (option.jumpToParameter === "stop") {
                            // Stop questionnaire, a 'stop' jump has been selected
                            props.onDone?.(true);
                            return;
                        }

                        // Find the question to jump to
                        newMinIndex = questions.findIndex((e) => e.parameter === (option as { jumpToParameter?: string }).jumpToParameter);
                    }
                }

                if (newMinIndex < 0) {
                    console.error("could not find question to jump to by id, just going to next one");
                    newMinIndex = maxIndex;
                }
            }
        }

        let untilIncluding = -1;
        for (let i = newMinIndex; i < questions.length; i++) {
            let question = questions[i];
            if (isJumpableQuestion(question)) {
                untilIncluding = i;
                break;
            }
        }

        setMinIndex(newMinIndex);
        setMaxIndex(untilIncluding < 0 ? questions.length : untilIncluding + 1);
    }

    useEffect(() => {
        setNextQuestions();
    }, []);

    let currentQuestionsAnswered = true;
    let currentQuestions = [];
    for (let i = minIndex; i < maxIndex; i++) {
        let question = questions[i];
        if (!question.optional && !["title", "image"].includes(question.type) && !question.answers?.[0]) currentQuestionsAnswered = false;
        currentQuestions.push(question);
    }

    return (
        <div>
            {currentQuestions.map((question, i) => (
                <QuestionAnswer
                    accessToken={props.accessToken}
                    onChange={(value) => {
                        let newQuestion = { ...question };
                        if (!newQuestion.answers || newQuestion.answers.length <= 0) {
                            newQuestion.answers = [{ valueJson: value } as any];
                        } else {
                            newQuestion.answers[0].valueJson = value;
                        }

                        let questionIndex = minIndex + i;
                        let arr = [...questions];
                        arr[questionIndex] = newQuestion;
                        setQuestions(arr);
                    }}
                    initialValue={question.answers && question.answers.length > 0 ? question.answers[0].valueJson : undefined}
                    key={question.parameter}
                    question={question}
                />
            ))}

            <ButtonContainer>
                <Button
                    loading={props.loading}
                    disabled={!currentQuestionsAnswered || props.loading}
                    style={{ margin: "1em 0", display: "block" }}
                    icon={faArrowRight}
                    onClick={setNextQuestions}>
                    {maxIndex === questions.length ? language("apply-next-step-finish") : language("apply-next")}
                </Button>
            </ButtonContainer>
            {!currentQuestionsAnswered && <p>{language("apply-unanswered-questions")}</p>}

            {false && minIndex > 0 && (
                <ButtonContainer>
                    <Button
                        disabled={props.loading}
                        color="#666"
                        style={{ margin: "1em 0", display: "block" }}
                        icon={faArrowLeft}
                        onClick={setPreviousQuestions}>
                        {language("apply-previous")}
                    </Button>
                </ButtonContainer>
            )}
        </div>
    );
}
